// Module dependencies
import pubSub from 'app/common/lib/PubSub';
import ResourceFactory from 'app/common/lib/ResourceFactory';
import SocialConnect from 'app/common/lib/SocialConnect';
import timer from 'app/common/lib/timer';
import user from 'app/common/lib/User';

/**
 * @type {number}
 * @const
 */
const FIND_FRIENDS_TRY_AGAIN_POLL_INTERVAL = 1000;

/**
 * @type {number}
 * @const
 */
const FIND_FRIENDS_TRY_AGAIN_API_ERROR_CODE = 110;

/**
 * Manages a user's social network settings, lets user connect/disconnect
 * from an external social network.
 *
 * @constructor
 */
function SocialNetworkSettingsManager() {}

Object.assign(SocialNetworkSettingsManager.prototype, {
  /**
   * Handle connecting/disconnecting to a social network, taking care of
   * calling both external and internal connect logic.
   *
   * @param {string} network Which social network, e.g. 'facebook'; use
   *     constants like SocialConnect.FACEBOOK if possible.
   * @param {boolean} connect Whether to connect or disconnect.
   * @param {{success:(function(!Object)|undefined),
   *          error:(function(!Object)|undefined),
   *          complete:(function()|undefined)}} callback
   */
  handleConnect(network, connect, callback) {
    if (connect && !user.isLimitedLogin()) {
      const externalConnectCallback = {
        success: function(connectData) {
          const options = { network, data: connectData };
          this._internalConnectNetwork(connect, options, callback);
        }.bind(this),
        error(connectData) {
          if (callback.error) {
            callback.error(connectData);
          }
        },
        complete() {
          if (callback.complete) {
            callback.complete();
          }
        },
      };
      this._externalConnectNetwork(network, connect, externalConnectCallback);
    } else {
      const options = { network };
      this._internalConnectNetwork(connect, options, callback);
    }
  },

  /**
   * Update the social network connect status for the current user.
   */
  updateConnectStatus() {
    const userResource = ResourceFactory.create('UserResource', {
      user_id: user.get('id'),
      username: user.get('username'),
      field_set_key: 'social_connect',
    });
    userResource.callGet().then((response) => {
      const userData = /** @type {Object|null} */ (response.resource_response.data);

      user.set(userData);
      pubSub.instance.publish(
        PCONST.PUBSUB_CHANNEL_CURRENT_USER,
        PCONST.PUBSUB_TOPIC_SOCIAL_NETWORK_UPDATED,
        userData,
      );
    });
  },

  /**
   * Handle external connect/disconnect logic.
   *
   * @param {string} network Which social network, e.g. 'facebook'; use
   *     constants like SocialConnect.FACEBOOK if possible.
   *  // TODO(chris): Make this an enum type instead of a string.
   * @param {boolean} connect Whether to connect or disconnect.
   * @param {{success:(function(!Object)|undefined),
   *          error:(function(!Object)|undefined),
   *          complete:(function()|undefined),
        rerequest_facebook_email:boolean}} options
   * @private
   */
  _externalConnectNetwork(network, connect, options) {
    // Disconnecting is a no-op externally... at least for now.
    if (!connect) {
      if (options.success) {
        options.success({});
      }
      return;
    }

    SocialConnect.instance().connect(
      network,
      options,
    );
  },

  /**
   * Handle internal connect/disconnect logic.
   *
   * @param {boolean} connect Whether to connect or disconnect.
   * @param {!Object.<string,*>} options Information about which network
   *     and any data needed for connecting.
   * @param {{success:(function(!Object)|undefined),
   *          error:(function(!Object)|undefined),
   *          complete:(function()|undefined)}} callback
   * @private
   */
  _internalConnectNetwork(connect, options, callback) {
    const resource = ResourceFactory.create('UserSocialNetworkResource', options);
    (connect ? resource.callCreate() : resource.callDelete())
      .then(
        (resourceResponse) => {
          this.updateConnectStatus();
          if (callback.success) {
            callback.success(resourceResponse);
          }
        },
        (error) => {
          if (callback.error && error instanceof Object) {
            callback.error(error);
          }
        },
      )
      .finally(() => {
        if (callback.complete) {
          callback.complete();
        }
      });
  },

  /**
   * Map network name to friends resource.
   * @param {string} network Which social network, e.g. 'facebook'; use
   *     constants like SocialConnect.FACEBOOK if possible.
   * @private
   */
  _getResourceNameForNetworkFriends(network) {
    let resourceName = null;
    switch (network) {
      case SocialConnect.FACEBOOK:
        resourceName = 'UserFacebookFriendsResource';
        break;
      case SocialConnect.TWITTER:
        resourceName = 'UserTwitterFriendsResource';
        break;
      case SocialConnect.GOOGLE:
        resourceName = 'UserGmailFriendsResource';
        break;
      case SocialConnect.MICROSOFT:
        resourceName = 'UserMicrosoftFriendsResource';
        break;
      default:
        return undefined;
    }
    return resourceName;
  },

  /**
   * Keep polling the API while it is returning the error code for "try
   * again", and when the resource call finally succeeds, call the success
   * callback.
   *
   * @param {string} network Which social network to fetch friends from,
   *     e.g. 'facebook'; use constants like SocialConnect.FACEBOOK if
   *     possible.
   * @param {Object=} options The options for this function
   *     options:
   *          'success': {function(Resource)} Called when the resource's
   *              data is available.
   *          'error': {function()} Called if something goes wrong.
   *          'resourceOptions': {Object=} Options to pass to the resource.
   */
  pollForFriends(network, options) {
    options = options || {};
    const resourceOptions = options.resourceOptions || {};

    const resourceName = this._getResourceNameForNetworkFriends(network);
    if (!resourceName) {
      return;
    }

    const resource = ResourceFactory.create(resourceName, resourceOptions);
    resource.callGet({ showError: false }).then(
      () => {
        const callback = options.success;
        if (callback) {
          callback(resource);
        }
      },
      (error) => {
        if (error.api_error_code === FIND_FRIENDS_TRY_AGAIN_API_ERROR_CODE) {
          timer.setTimeout(() => {
            this.pollForFriends(network, options);
          }, FIND_FRIENDS_TRY_AGAIN_POLL_INTERVAL);
        } else {
          const callback = options.error;
          if (callback) {
            callback();
          }
        }
      },
    );
  },
});

/**
 * @type {SocialNetworkSettingsManager}
 */
let socialNetworkSettingsMgrInstance = null;

SocialNetworkSettingsManager.instance = function() {
  if (!socialNetworkSettingsMgrInstance) {
    socialNetworkSettingsMgrInstance = new SocialNetworkSettingsManager();
  }
  return socialNetworkSettingsMgrInstance;
};

export default SocialNetworkSettingsManager;
